import type {
  PreviewEndpoint,
  PreviewLookups,
  PreviewMilestone,
  PreviewSegment,
  PreviewTranslate,
  ShowRoutePreviewPayload,
} from '@typings/route-preview.ts';
import { getPoiIconId } from '@utils/poi.ts';

import type { Coordinate } from '../types.ts';
import { pointAtHalfLength } from './pointAtHalfLength.ts';
import { isReturnOnSameCorridor, type TransitCorridor } from './transitCorridor.ts';

const FACILITIES =
  /^(?:wayfinding\.)?(elevator|ramp|accessiblestairs|stairs|escalator|movingwalkway)(?:\.(?:up|down))?$/;
const getTransitKind = (segment: PreviewSegment): 'train' | 'bus' | null =>
  segment.segmentType === 'nav.train' ? 'train' : segment.segmentType === 'nav.transit' ? 'bus' : null;
const isSecurityBadge = (name: string) =>
  name === 'security' || name === 'wayfinding.security' || name.startsWith('wayfinding.security.');
const getFacilityLabel = (facility: string, T: PreviewTranslate) => {
  switch (facility) {
    case 'elevator':
      return T('wayfinder:Elevator');
    case 'ramp':
      return T('wayfinder:Ramp');
    case 'accessiblestairs':
      return T('wayfinder:AccessibleStairs');
    case 'stairs':
      return T('wayfinder:Stairs');
    case 'escalator':
      return T('wayfinder:Escalator');
    default:
      return T('getDirectionsFromTo:Moving walkway');
  }
};

const validCoordinate = (coordinate: Coordinate) =>
  Array.isArray(coordinate) &&
  coordinate.length === 2 &&
  coordinate.every(Number.isFinite) &&
  Math.abs(coordinate[0]) <= 180 &&
  Math.abs(coordinate[1]) <= 90;

const normalizeBadgeName = (canonical: string) =>
  canonical === 'walk.movingwalkway' ? 'wayfinding.movingwalkway' : canonical;

const getOrdinal = (segment: PreviewSegment) => {
  const match = /^ordinal:\s*(-?\d+(?:\.\d+)?)$/.exec(segment.ordinalId);
  return match ? Number(match[1]) : null;
};

/** Derives a small route-ordered milestone set without mutating route data or parsing translated instructions. */
export async function buildMilestones(
  { routes, endpoints, endpointIds, artworkFormats }: ShowRoutePreviewPayload,
  T: PreviewTranslate,
  lookups: PreviewLookups,
): Promise<PreviewMilestone[]> {
  if (endpoints.length < 2) {
    throw new Error('Preview requires an origin and destination.');
  }
  for (const endpoint of endpoints) {
    if (!endpoint || !validCoordinate([endpoint.lng, endpoint.lat]) || !Number.isFinite(endpoint.ordinal)) {
      throw new Error('Preview requires valid coordinates and a floor ordinal for every endpoint.');
    }
  }
  if (routes.length !== endpoints.length - 1) {
    throw new Error('Preview requires one selected route leg between each pair of endpoints.');
  }
  const milestones: PreviewMilestone[] = [];
  const transitCorridors: TransitCorridor[] = [];
  const seen = new Set<string>();
  const add = (milestone: PreviewMilestone) => {
    if (!validCoordinate(milestone.coordinates) || !Number.isFinite(milestone.ordinal)) {
      return;
    }
    const key = `${milestone.kind}:${milestone.icon}:${milestone.ordinal}:${milestone.coordinates.join(',')}:${milestone.stop ?? ''}`;
    if (!seen.has(key)) {
      seen.add(key);
      milestones.push({ ...milestone, artworkFormat: artworkFormats?.[milestone.icon] ?? 'glyph' });
    }
  };

  const endpointMilestone = async (endpoint: PreviewEndpoint, index: number) => {
    const kind = index === 0 ? 'origin' : index === endpoints.length - 1 ? 'destination' : 'stop';
    const sourceId = endpointIds?.[index];
    const poiId =
      endpoint.poiId ?? (typeof sourceId === 'string' || typeof sourceId === 'number' ? sourceId : undefined);
    const poi = poiId != null ? await lookups.poi(poiId) : undefined;
    const name =
      poi?.name ||
      endpoint.title ||
      (kind === 'origin'
        ? T('wayfinder:Starting Point')
        : kind === 'destination'
          ? T('getDirectionsFromTo:Destination')
          : T('getDirectionsFromTo:Stop _stop_', { stop: index }));
    const context = kind === 'origin' ? T('getDirectionsFromTo:Start at') : T('getDirectionsFromTo:Arrive at');
    add({
      id: `endpoint-${index}`,
      kind,
      coordinates: [endpoint.lng, endpoint.lat],
      ordinal: endpoint.ordinal,
      icon: kind === 'origin' ? 'wayfinding.start' : poi?.category ? getPoiIconId(poi) : 'wayfinding.end',
      label:
        kind === 'stop'
          ? T('getDirectionsFromTo:Stop _stop_: _location_', { stop: index, location: name })
          : kind === 'origin'
            ? T('getDirectionsFromTo:Start at _location_', { location: name })
            : T('getDirectionsFromTo:Arrive at _location_', { location: name }),
      ...(kind === 'stop' ? { stop: index } : { callout: { context, primary: name } }),
    });
  };

  // Start required metadata lookups together; the controller owns request caching.
  const prefetch: Promise<unknown>[] = [];
  endpoints.forEach((endpoint, index) => {
    const id = endpoint.poiId ?? endpointIds?.[index];
    if (typeof id === 'string' || typeof id === 'number') {
      prefetch.push(lookups.poi(id));
    }
  });
  routes.forEach(leg =>
    leg.segments.forEach(segment => {
      const transit = getTransitKind(segment);
      const security =
        segment.segmentType === 'nav.secure' || segment.badges?.some(badge => isSecurityBadge(badge.canonicalName));
      if ((transit || security) && segment.poiId != null) {
        prefetch.push(lookups.poi(segment.poiId));
      }
      if (transit && segment.transit) {
        prefetch.push(lookups.location(segment.transit.entry), lookups.location(segment.transit.exit));
      }
    }),
  );
  await Promise.all(prefetch);
  await endpointMilestone(endpoints[0], 0);
  for (const [legIndex, leg] of routes.entries()) {
    // A ride can be split at graph/floor boundaries; keep one boarding-to-alighting milestone.
    const segments: { segment: PreviewSegment; originalIndex: number }[] = [];
    for (const [originalIndex, segment] of leg.segments.entries()) {
      const previousEntry = segments.at(-1);
      const previous = previousEntry?.segment;
      const transit = getTransitKind(segment);
      if (
        transit &&
        previous?.segmentType === segment.segmentType &&
        previous.poiId === segment.poiId &&
        previous.coordinates.at(-1)?.join(',') === segment.coordinates[0]?.join(',')
      ) {
        segments[segments.length - 1] = {
          originalIndex: previousEntry!.originalIndex,
          segment: {
            ...previous,
            coordinates: [...previous.coordinates, ...segment.coordinates.slice(1)],
            ...(previous.transit && segment.transit
              ? { transit: { entry: previous.transit.entry, exit: segment.transit.exit } }
              : {}),
          },
        };
      } else {
        segments.push({ segment, originalIndex });
      }
    }
    for (const [position, { segment, originalIndex: segmentIndex }] of segments.entries()) {
      // A portal is marked once, halfway along its path from where the previous segment ends.
      const previousEnd = segments[position - 1]?.segment.coordinates.at(-1);
      const portalPath = previousEnd ? [previousEnd, ...segment.coordinates] : segment.coordinates;
      const hasValidPortalPath = portalPath.length > 0 && portalPath.every(validCoordinate);
      // The walk into a portal repeats the portal's badge; the portal segment's own badge represents it.
      const leadsIntoPortal = !segment.isPortal && segments[position + 1]?.segment.isPortal === true;
      const kind = getTransitKind(segment);
      const ordinal =
        kind && Number.isFinite(segment.transit?.entry.ordinal) ? segment.transit!.entry.ordinal! : getOrdinal(segment);
      if (ordinal === null) {
        continue;
      }
      if (kind) {
        if (!hasValidPortalPath) {
          continue;
        }
        const floorContext = segment.transit
          ? JSON.stringify(
              [
                segment.transit.entry.floorId ?? segment.transit.entry.ordinal ?? segment.ordinalId,
                segment.transit.exit.floorId ?? segment.transit.exit.ordinal ?? segment.ordinalId,
              ].sort(),
            )
          : segment.ordinalId;
        const corridor: TransitCorridor = { kind, serviceId: segment.poiId, floorContext, path: portalPath };
        const duplicateReturn = transitCorridors.some(previous => isReturnOnSameCorridor(previous, corridor));
        transitCorridors.push(corridor);
        if (duplicateReturn) {
          continue;
        }
        const coordinates = pointAtHalfLength(portalPath);
        const service = segment.poiId != null ? await lookups.poi(segment.poiId) : undefined;
        const mode = kind === 'train' ? T('wayfinder:Train') : T('wayfinder:Bus');
        const [entry, exit] = segment.transit
          ? await Promise.all([lookups.location(segment.transit.entry), lookups.location(segment.transit.exit)])
          : [];
        const journey =
          entry && exit && entry !== exit
            ? T('getDirectionsFromTo:_from_ → _to_', { from: entry, to: exit })
            : undefined;
        const serviceName = service?.name || mode;
        add({
          id: `leg-${legIndex}-segment-${segmentIndex}-transit`,
          kind,
          coordinates,
          ordinal: segment.transit?.entry.ordinal ?? ordinal,
          icon: `wayfinding.${kind}`,
          label: journey
            ? T('getDirectionsFromTo:_from_ to _to_ by _service_', { from: entry, to: exit, service: serviceName })
            : serviceName,
          callout: journey
            ? { primary: journey, secondary: serviceName, journey: { from: entry!, to: exit! } }
            : { primary: serviceName },
        });
        continue;
      }
      const badges =
        segment.segmentType === 'nav.secure'
          ? [{ canonicalName: 'wayfinding.security', coordinates: segment.coordinates.at(-1)! }]
          : (segment.badges ?? []);
      for (const [badgeIndex, badge] of badges.entries()) {
        const canonical = badge.canonicalName;
        if (canonical.includes(' ') || !validCoordinate(badge.coordinates)) {
          continue;
        }
        const normalized = normalizeBadgeName(canonical);
        const security = isSecurityBadge(normalized);
        const facility = FACILITIES.exec(normalized);
        if (!security && !facility) {
          continue;
        }
        if (facility && leadsIntoPortal) {
          continue;
        }
        const coordinates =
          facility && segment.isPortal && hasValidPortalPath ? pointAtHalfLength(portalPath) : badge.coordinates;
        const poi = security && segment.poiId != null ? await lookups.poi(segment.poiId) : undefined;
        const matchingStep = leg.steps?.[segmentIndex];
        const step =
          (matchingStep?.icon?.startsWith('wayfinding.security') ? matchingStep : undefined) ??
          leg.steps?.find(
            candidate =>
              candidate.icon?.startsWith('wayfinding.security') &&
              candidate.poiId != null &&
              candidate.poiId === segment.poiId,
          );
        const facilityName = facility?.[1];
        const label = security
          ? step?.primaryText || poi?.name || T('wayfinder:Security Checkpoint')
          : getFacilityLabel(facilityName!, T);
        add({
          id: `leg-${legIndex}-segment-${segmentIndex}-badge-${badgeIndex}`,
          kind: security ? 'security' : 'facility',
          coordinates,
          ordinal,
          icon: normalized,
          label,
          ...(security ? { callout: { primary: label } } : {}),
          ...(facilityName === 'accessiblestairs' ? { fallbackLabel: label } : {}),
        });
      }
    }
    const endpointIndex = legIndex + 1;
    if (endpointIndex < endpoints.length) {
      await endpointMilestone(endpoints[endpointIndex], endpointIndex);
    }
  }
  return milestones;
}
